import { useMutation } from '@tanstack/react-query';

/**
 * Proxypass endpoint used by the SMS webhook API Builder to bypass CORS
 * when probing a customer's SMS gateway. Mirrors the legacy `PROXY_URL`
 * constant. Override at build time via `VITE_PROXY_URL`.
 */
export const PROXY_URL =
  import.meta.env.VITE_PROXY_URL ?? 'https://obd3api.nspl.cloud/proxypass';

interface TestProxyRequestPayload {
  url: string;
  method: string;
  /** JSON-stringified header map. */
  headers: string;
  /** Raw body string — null/empty for GET. */
  body: string | null;
}

export interface ProxyResponse {
  status: number;
  statusText: string;
  headers: Record<string, string>;
  data: unknown;
}

/**
 * Live-test a customer's SMS gateway via our proxy. The proxypass endpoint
 * accepts the target URL/method/headers/body, executes the call server-side,
 * and returns `{status, statusText, headers, data}`. Supports AbortController
 * via the `signal` option in `mutate`.
 */
export function useTestProxyRequest() {
  return useMutation({
    mutationFn: async ({
      payload,
      signal,
    }: {
      payload: TestProxyRequestPayload;
      signal?: AbortSignal;
    }): Promise<ProxyResponse> => {
      const res = await fetch(`${PROXY_URL}/proxy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal,
      });
      const contentType = res.headers.get('content-type') ?? '';
      const data =
        contentType.includes('application/json')
          ? await res.json()
          : await res.text();
      const obj = data as ProxyResponse;
      return {
        status: obj.status,
        statusText: obj.statusText,
        headers: obj.headers,
        data: obj.data,
      };
    },
  });
}
